import { useEffect, useMemo, useState } from 'react';
import { businessStore } from 'entities/index';
import { IEmployeeInfo } from 'shared/api/business/interfaces/employee-info.interface';
import { IServicesCard } from 'shared/api/booking/interfaces/services-list.interface';
import { IServiceCategory } from 'shared/api/booking/interfaces/service-categories.interface';

export type FilterMode = 'category' | 'employee';

export interface UseServicesCardReturn {
  inputValue: string;
  setInputValue: (v: string) => void;
  filterMode: FilterMode;
  setFilterMode: (v: FilterMode) => void;
  employeeOptions: string[];
  selectedEmployeeId: string | null;
  handleEmployeeChange: (option: string) => void;
  allEmployees: IEmployeeInfo[];
  services: IServicesCard[];
  categories: IServiceCategory[];
  servicesByCategory: (list: IServicesCard[]) => {
    category: IServiceCategory;
    services: IServicesCard[];
  }[];
  getUncategorized: (list: IServicesCard[]) => IServicesCard[];
  getEmployeeServices: (id: string) => IServicesCard[];
  categoriesOpenStatus: Array<boolean | undefined>;
  toggleCategory: (index: number) => void;
  isUncategorizedOpen: boolean;
  setIsUncategorizedOpen: (v: boolean) => void;
  employeesOpenStatus: Record<string, boolean>;
  toggleEmployee: (id: string) => void;
  activeModal: 'createService' | 'createCategory' | 'changeCategories' | null;
  setActiveModal: (
    v: 'createService' | 'createCategory' | 'changeCategories' | null
  ) => void;
}

export function useServicesCard(): UseServicesCardReturn {
  const [inputValue, setInputValue] = useState('');
  const [filterMode, setFilterMode] = useState<FilterMode>('category');
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<string | null>(
    null
  );
  const [activeModal, setActiveModal] = useState<
    'createService' | 'createCategory' | 'changeCategories' | null
  >(null);
  const [categoriesOpenStatus, setCategoriesOpenStatus] = useState<
    Array<boolean | undefined>
  >([]);
  const [isUncategorizedOpen, setIsUncategorizedOpen] = useState(true);
  const [employeesOpenStatus, setEmployeesOpenStatus] = useState<
    Record<string, boolean>
  >({});

  const pointId = businessStore.pointId;

  useEffect(() => {
    if (pointId) {
      businessStore.initServicesList(pointId);
      businessStore.initServiceCategories(pointId);
      businessStore.initEmployeesWidgetData(pointId);
    }
  }, [pointId]);

  const categories = businessStore.serviceCategories ?? [];
  const services = businessStore.servicesList ?? [];

  useEffect(() => {
    const len = categories.length;
    setCategoriesOpenStatus(Array(len).fill(true));
  }, [categories]);

  useEffect(() => {
    if (filterMode === 'category') {
      setSelectedEmployeeId(null);
    }
  }, [filterMode]);

  const allEmployees = useMemo(() => {
    const map = new Map<string, IEmployeeInfo>();
    services.forEach((s) => {
      s.employees.forEach((e) => {
        if (!map.has(e.id)) {
          map.set(e.id, { ...e });
        }
      });
    });
    return Array.from(map.values());
  }, [services]);

  const employeeOptions = ['Все сотрудники', ...allEmployees.map((e) => e.fio)];

  const employeeNameToId: Record<string, string> = {};
  allEmployees.forEach((e) => {
    employeeNameToId[e.fio] = e.id;
  });

  const handleEmployeeChange = (option: string) => {
    if (option === 'Все сотрудники') {
      setSelectedEmployeeId(null);
    } else {
      setSelectedEmployeeId(employeeNameToId[option] ?? null);
    }
  };

  const getEmployeeServices = (employeeId: string) => {
    return services.filter((s) => s.employees.some((e) => e.id === employeeId));
  };

  const servicesByCategory = (serviceList: IServicesCard[]) => {
    return categories
      .map((cat) => ({
        category: cat,
        services: serviceList.filter((s) => s.category?.id === cat.id),
      }))
      .filter((g) => g.services.length > 0);
  };

  const getUncategorized = (serviceList: IServicesCard[]) => {
    return serviceList.filter((s) => !s.category?.id);
  };

  const toggleEmployee = (employeeId: string) => {
    setEmployeesOpenStatus((prev) => ({
      ...prev,
      [employeeId]: !prev[employeeId],
    }));
  };

  useEffect(() => {
    setEmployeesOpenStatus((prev) => {
      const status = { ...prev };
      let changed = false;
      allEmployees.forEach((e) => {
        if (!(e.id in status)) {
          status[e.id] = true;
          changed = true;
        }
      });
      return changed ? status : prev;
    });
  }, [allEmployees]);

  const toggleCategory = (index: number) => {
    setCategoriesOpenStatus((prev) => {
      const newState = [...prev];
      newState[index] = !newState[index];
      return newState;
    });
  };

  return {
    inputValue,
    setInputValue,
    filterMode,
    setFilterMode,
    employeeOptions,
    selectedEmployeeId,
    handleEmployeeChange,
    allEmployees,
    services,
    categories,
    servicesByCategory,
    getUncategorized,
    getEmployeeServices,
    categoriesOpenStatus,
    toggleCategory,
    isUncategorizedOpen,
    setIsUncategorizedOpen,
    employeesOpenStatus,
    toggleEmployee,
    activeModal,
    setActiveModal,
  };
}
